import React from 'react';
import styled from '@2pulse/responsive';
import { MainLayout, Triptych } from '@app/components';
import { FinanceHeader } from '@app/features/FinanceHeader';
import { normalize } from '@app/helpers';
import { useResourceScreenHelpers } from '@app/hooks';
import { useAppSelector } from '@app/store/hooks';
import { IFiscalRegisterSummary } from '@tickeat/types';
import { FiscalRegisterList, FiscalRegisterCard, FiscalArchives, FiscalEcoleOrders, FiscalAccountingExports } from '@app/features/Fiscal';

// Page « Conformité NF525 » (chantier 9c-B) : caisses de la boutique, fiche
// d'une caisse, archives de la caisse. Même triptyque que Services.
export const FiscalScreen = () => {
  const { triptych, selectedItem } = useResourceScreenHelpers<IFiscalRegisterSummary>();

  // UNE CAISSE APPARTIENT À UNE BOUTIQUE (retour du 9 sept.). Toutes les routes
  // `/fiscal` de la gateway lisent l'en-tête `x-shop`, que `rootApi` ne pose que
  // si une boutique est sélectionnée : sans elle, la page affichait « Aucun
  // élément à afficher » puis « Bad Request Exception » — deux messages qui ne
  // disaient pas ce qu'il fallait faire. On le dit, et on ne monte rien qui
  // interrogerait l'engine.
  const boutique = useAppSelector((state) => state.app.selectedShop);

  if (!boutique) {
    return (
      <MainLayout header={<FinanceHeader />}>
        <Vide>
          <VideTitre>Choisissez un restaurant</VideTitre>
          <VideTexte>
            Les caisses NF525 sont propres à chaque restaurant : sélectionnez-en un dans le menu latéral pour voir ses caisses, ses archives et ses commandes d’entraînement.
          </VideTexte>
        </Vide>
      </MainLayout>
    );
  }

  return (
    <MainLayout header={<FinanceHeader />}>
      <Triptych
        list={(
          <FiscalRegisterList
            selectedRowItem={selectedItem}
            onRowPress={(item) => triptych.close({ data: item, mode: 'UPDATE' })}
            onShow={(item) => triptych.open({ data: item, mode: 'UPDATE' })} />
        )}
        card={(
          <FiscalRegisterCard
            register={selectedItem}
            toggle={(mode) => mode === 'UPDATE' ? triptych.open({ data: selectedItem, mode: 'UPDATE' }) : triptych.close()} />
        )}
        form={(
          <FiscalArchives
            registerId={selectedItem?.registerId}
            enTete={(
              <>
                <FiscalEcoleOrders />
                {/* Sceau et piste d'audit des exports comptables (E-07 volet 2, tâche 14) :
                    boutique-scopé comme les commandes d'entraînement ci-dessus, indépendant
                    de la caisse sélectionnée. */}
                <FiscalAccountingExports />
              </>
            )} />
        )} />
    </MainLayout>
  );
};

// Même vide que `SimpleList` (« Aucun élément à afficher ») : centré, gris,
// Work Sans — avec le titre qui manquait.
const Vide = styled.View({
  flex: 1,
  alignItems: 'center',
  justifyContent: 'center',
  paddingHorizontal: normalize(55),
  rowGap: normalize(13),
});

const VideTitre = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  letterSpacing: -0.1,
  fontSize: normalize(30),
  fontWeight: '700',
  textAlign: 'center',
  // Ce vide-ci est rendu HORS du `SubContainer` sombre : le fond est le
  // babypowder de l'application, comme le vide de `SimpleList` (oxfordblue).
  color: theme.colors.oxfordblue,
}));

const VideTexte = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(20),
  lineHeight: normalize(28),
  fontWeight: '500',
  textAlign: 'center',
  maxWidth: normalize(680),
  color: theme.colors.satanGray,
}));
